<template>
  <div class="share-legend-editor mt-4">
    <div class="d-flex align-center justify-space-between mb-3">
      <div style="font-weight: bold; font-size: 16px">Chú giải bản đồ</div>
      <div>
        <v-menu offset-y bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small color="primary" class="text-none" v-bind="attrs" v-on="on" :disabled="!mapShareId">
              <v-icon left small>mdi-plus</v-icon>
              Thêm mới
              <v-icon right small>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="openDialog('list')">
              <v-icon small color="primary" class="mr-2">mdi-format-text</v-icon>
              <v-list-item-title>Chú giải chữ</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$refs.fileInput.click()" :disabled="loading">
              <v-icon small color="secondary" class="mr-2">mdi-image-plus</v-icon>
              <v-list-item-title>Chú giải ảnh</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <input type="file" ref="fileInput" style="display: none" accept="image/png, image/jpeg, image/svg+xml"
          @change="onFileSelected" />
        <input type="file" ref="fileReplaceInput" style="display: none" accept="image/png, image/jpeg, image/svg+xml"
          @change="onFileReplaced" />
      </div>
    </div>

    <v-alert v-if="!mapShareId" type="info" dense text>
      Vui lòng lưu bản đồ lần đầu tiên để có thể thêm chú giải.
    </v-alert>

    <v-card outlined v-if="mapShareId">
      <v-list dense>
        <draggable v-model="legends" @change="onDragChange" handle=".drag-handle">
          <div v-for="(item, index) in legends" :key="item.id">
            <v-list-item class="legend-list-item">
              <v-list-item-action class="mr-2">
                <v-icon class="drag-handle" style="cursor: grab;" title="Kéo thả để sắp xếp">mdi-drag</v-icon>
              </v-list-item-action>

            <v-list-item-content>
              <div v-if="item.type === 'list'" class="d-flex align-center">
                <div class="color-box" :style="{ backgroundColor: item.color }"></div>
                <span class="ml-3">{{ item.title }}</span>
              </div>
              <div v-else-if="item.type === 'image'" class="d-flex align-center">
                <v-img :src="getImageUrl(item.image_path)" max-width="150" max-height="80" contain
                  position="left"></v-img>
                <span class="ml-3 text-caption grey--text">(Ảnh)</span>
              </div>
            </v-list-item-content>

            <v-list-item-action class="flex-row align-center">
              <v-btn icon small color="primary" @click="editItem(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="error" class="ml-1" @click="deleteItem(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="index < legends.length - 1"></v-divider>
        </div>
        </draggable>
        <v-list-item v-if="legends.length === 0">
          <v-list-item-content>
            <div class="text-center grey--text py-4">Chưa có chú giải nào</div>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>

    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">{{ editId ? 'Sửa chú giải' : 'Thêm chú giải' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" lazy-validation>
            <v-text-field v-model="formData.title" label="Nhãn chú giải" outlined dense
              :rules="[v => !!v || 'Vui lòng nhập nhãn']"></v-text-field>
            <div class="mt-2 mb-1">Màu sắc</div>
            <field-group onlyColor v-model="formData.color" type-input="field-color-picker"></field-group>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialog = false">Hủy</v-btn>
          <v-btn color="primary" @click="saveListLegend" :loading="loading">Lưu</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { API_MAPSHARE_LEGEND } from '@api'
import draggable from 'vuedraggable'

export default {
  name: 'ShareLegendEditor',
  components: { draggable },
  props: {
    mapShareId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      legends: [],
      loading: false,
      dialog: false,
      editId: null,
      formData: {
        title: '',
        color: '#000000'
      },
      replacingItem: null
    }
  },
  watch: {
    mapShareId(newVal) {
      if (newVal) {
        this.fetchLegends()
      } else {
        this.legends = []
      }
    }
  },
  mounted() {
    if (this.mapShareId) {
      this.fetchLegends()
    }
  },
  methods: {
    getImageUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      // Handle relative storage paths by stripping /api from VUE_APP_API_URL
      let baseUrl = process.env.VUE_APP_API_URL || ''
      if (baseUrl.endsWith('/api/') || baseUrl.endsWith('/api')) {
        baseUrl = baseUrl.replace(/\/api\/?$/, '')
      }
      return baseUrl ? `${baseUrl}${path}` : path
    },
    async fetchLegends() {
      if (!this.mapShareId) return
      this.loading = true
      try {
        const res = await API_MAPSHARE_LEGEND.list(this.mapShareId)
        if (Array.isArray(res)) {
          this.legends = res
        } else if (res && res.data) {
          this.legends = res.data
        } else {
          this.legends = []
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    openDialog(type, item = null) {
      if (type === 'list') {
        if (item) {
          this.editId = item.id
          this.formData.title = item.title
          this.formData.color = item.color || '#000000'
        } else {
          this.editId = null
          this.formData.title = ''
          this.formData.color = '#000000'
        }
        this.dialog = true
        if (this.$refs.form) this.$refs.form.resetValidation()
      }
    },
    async saveListLegend() {
      if (!this.$refs.form.validate()) return
      this.loading = true
      try {
        const payload = {
          map_share_id: this.mapShareId,
          type: 'list',
          title: this.formData.title,
          color: this.formData.color
        }

        if (this.editId) {
          await API_MAPSHARE_LEGEND.update(this.editId, payload)
        } else {
          await API_MAPSHARE_LEGEND.create(payload)
        }
        this.dialog = false
        await this.fetchLegends()
      } catch (error) {
        this.$toast.error('Có lỗi xảy ra khi lưu chú giải')
      } finally {
        this.loading = false
      }
    },
    async onFileSelected(e) {
      const file = e.target.files[0]
      if (!file) return
      this.$refs.fileInput.value = ''

      const formData = new FormData()
      formData.append('map_share_id', this.mapShareId)
      formData.append('type', 'image')
      formData.append('image', file)

      this.loading = true
      try {
        await API_MAPSHARE_LEGEND.create(formData)
        await this.fetchLegends()
      } catch (error) {
        this.$toast.error('Lỗi khi tải ảnh chú giải')
      } finally {
        this.loading = false
      }
    },
    editItem(item) {
      if (item.type === 'list') {
        this.openDialog('list', item)
      } else if (item.type === 'image') {
        this.replacingItem = item
        this.$refs.fileReplaceInput.click()
      }
    },
    async onFileReplaced(e) {
      const file = e.target.files[0]
      if (!file || !this.replacingItem) return
      this.$refs.fileReplaceInput.value = ''

      const formData = new FormData()
      formData.append('type', 'image')
      formData.append('image', file)

      this.loading = true
      try {
        await API_MAPSHARE_LEGEND.update(this.replacingItem.id, formData)
        await this.fetchLegends()
      } catch (error) {
        this.$toast.error('Lỗi khi thay thế ảnh')
      } finally {
        this.loading = false
        this.replacingItem = null
      }
    },
    async deleteItem(item) {
      if (!confirm('Bạn có chắc chắn muốn xóa chú giải này?')) return
      this.loading = true
      try {
        await API_MAPSHARE_LEGEND.delete(item.id)
        await this.fetchLegends()
      } catch (error) {
        this.$toast.error('Lỗi khi xóa chú giải')
      } finally {
        this.loading = false
      }
    },
    async onDragChange(event) {
      if (!event.moved) return
      
      const { element, newIndex } = event.moved
      
      try {
        await API_MAPSHARE_LEGEND.update(element.id, { order: newIndex })
      } catch (error) {
        this.$toast.error('Lỗi khi lưu vị trí')
        this.fetchLegends() // Rollback on error
      }
    }
  }
}
</script>

<style scoped>
.color-box {
  width: 24px;
  height: 24px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-list-item {
  padding: 0px 8px;
}
</style>
